import React, { useCallback, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { IClient } from '../../../utils/types/IClient';
import * as XLSX from 'xlsx';
import { SoldeClientDTO } from '../../../utils/types/objects/SoldeClientDTO';

ModuleRegistry.registerModules([AllCommunityModule]);

interface SoldeClientGridProps {
    soldesClients: SoldeClientDTO[];
}

const SoldeClientGrid: React.FC<SoldeClientGridProps> = ({ soldesClients }) => {
    const gridRef = useRef<AgGridReact>(null);

    const industryMap: { [key: number]: string } = {
        1: 'Provision',
        2: 'Crédit',
        [-1]: 'Cash',
    };

    const [columnDefs] = useState<ColDef<SoldeClientDTO>[]>([
        { field: 'nomClient', headerName: 'Nom client', sortable: true, filter: true },
        {
            field: 'industryC',
            headerName: 'Type client',
            sortable: true,
            filter: 'agSetColumnFilter',
            valueFormatter: (params) => industryMap[params.value] ?? 'Inconnu',
            filterParams: {
                valueFormatter: (params: any) => industryMap[params.value] ?? 'Inconnu',
            },
        },
        {
            field: 'soldeOuverture',
            headerName: 'Solde ouverture',
            sortable: true,
            valueFormatter: (params) => (params.value != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'paiementPOS',
            headerName: 'Paiement POS',
            sortable: true,
            valueFormatter: (params) => (params.value != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'paiementSAP',
            headerName: 'Paiement SAP',
            sortable: true,
            valueFormatter: (params) => (params.value != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'totalPaiement',
            headerName: 'Total Paiement',
            sortable: true,
            valueFormatter: (params) => (params.value != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'recharge',
            headerName: 'Recharge',
            sortable: true,
            valueFormatter: (params) => (params.value != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'livraisonsSansCarte',
            headerName: 'Livraisons sans carte',
            sortable: true,
            valueFormatter: (params) => (params.value != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'livraisonsAvecCarte',
            headerName: 'Livraisons avec carte',
            sortable: true,
            valueFormatter: (params) => (params.value != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'solde',
            headerName: 'Solde client',
            sortable: true,
            valueFormatter: (params) => (params.value != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
    ]);

    const myTheme = themeBalham.withParams({
        columnBorder: { style: 'dashed', width: '1px' },
    });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;

        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const keys = visibleCols.map((col) => col.getColId());
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        gridRef.current.api.exportDataAsCsv({
            fileName: `soldes_clients_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys: keys,
            suppressQuotes: false,
        });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;

        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const rows: IClient[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => rows.push(node.data));
        if (rows.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        const exportData = rows.map((row) => {
            const obj: Record<string, any> = {};
            visibleCols.forEach((col) => {
                const def = col.getColDef();
                const field = def.field;
                const header = def.headerName ?? field ?? '';
                if (!field) return;
                let value = (row as any)[field];
                // Appliquer formatage numérique/currency identique à la grille
                if (typeof value === 'number') {
                    value = value.toFixed(2);
                }
                obj[header] = value;
            });
            return obj;
        });

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'SoldesClients');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(wb, `soldes_clients_export_${timestamp}.xlsx`);
    }, []);

    return (
        <>
            <div className="" style={{ height: '100vh', width: '100%' }}>
                <div style={{ height: '100%', width: '100%' }}>
                    <AgGridReact ref={gridRef} theme={myTheme} getRowId={(params) => params.data.codeClient.toString()} rowData={soldesClients} columnDefs={columnDefs} pagination domLayout="normal" defaultColDef={{ resizable: true, editable: false, minWidth: 100, flex: 1 }} suppressExcelExport={true} enableCellTextSelection ensureDomOrder animateRows overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>" />
                </div>
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default SoldeClientGrid;
